import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  INSP_KINDS, INTERVALS, INTERVAL_UNITS, INSP_PRIORITIES, ONE_TIME_KINDS, previewNextDue,
} from "@/lib/inspectionConstants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const EMPTY = {
  title: "", kind: "wiederkehrende_pruefung", project_id: "", category: "", asset_ids: [], area: "", building: "", floor: "", room: "",
  manufacturer: "", model: "", serial_number: "", inventory_number: "", department: "", responsible: "",
  service_provider: "", contact: "", last_inspection_date: "", interval_type: "jaehrlich", interval_value: 0,
  interval_unit: "tage", next_due: "", priority: "normal", requires_protocol: false, notes: "",
};

function Field({ label, children }) {
  return <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>;
}

export default function InspectionForm({ open, onClose, onSaved, initial, categories, assets, projects = [], isAdmin = false }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = !!initial;
  const lockMaster = editing && !isAdmin;

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY, ...initial, asset_ids: initial.asset_ids || [] } : EMPTY);
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isOneTime = ONE_TIME_KINDS.has(form.kind);
  const preview = useMemo(
    () => previewNextDue(form.last_inspection_date, form.interval_type, form.interval_value, form.interval_unit),
    [form.last_inspection_date, form.interval_type, form.interval_value, form.interval_unit],
  );

  const toggleAsset = (id) => set("asset_ids", form.asset_ids.includes(id) ? form.asset_ids.filter((x) => x !== id) : [...form.asset_ids, id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Bezeichnung ist erforderlich");
    setSaving(true);
    try {
      const payload = { ...form, interval_value: parseInt(form.interval_value || 0, 10) };
      if (editing) await api.put(`/inspections/${initial.id}`, payload);
      else await api.post("/inspections", payload);
      toast.success(editing ? "Prüfung aktualisiert" : "Prüfung angelegt");
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head text-xl">{editing ? "Prüfung / Wartung bearbeiten" : "Neue Prüfung / Wartung"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-1" data-testid="inspection-form">
          {lockMaster ? (
            <div className="space-y-4" data-testid="insp-dates-only">
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">Als Benutzer können Sie nur die Termine eintragen. Die Stammdaten dieser Prüfung ändert der Admin.</div>
              <Field label="Bezeichnung"><Input value={form.title} disabled data-testid="insp-title" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Letztes Prüfdatum"><Input type="date" value={form.last_inspection_date || ""} onChange={(e) => set("last_inspection_date", e.target.value)} data-testid="insp-last-date" /></Field>
                <Field label="Nächste Fälligkeit"><Input type="date" value={form.next_due || ""} onChange={(e) => set("next_due", e.target.value)} data-testid="insp-next-due" /></Field>
              </div>
            </div>
          ) : (
          <>
          <Field label="Projekt zuordnen">
            <Select value={form.project_id || "__none"} onValueChange={(v) => set("project_id", v === "__none" ? "" : v)}>
              <SelectTrigger data-testid="insp-project"><SelectValue placeholder="Projekt wählen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— Ohne Projektzuordnung —</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-400 mt-1">Legt fest, zu welchem Projekt diese Wartung / Prüfung gehört.</p>
          </Field>
          <Field label="Bezeichnung *">
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="insp-title" placeholder="z. B. Prüfung Patientenlifter Station 2" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Art">
              <Select value={form.kind} onValueChange={(v) => set("kind", v)}>
                <SelectTrigger data-testid="insp-kind"><SelectValue /></SelectTrigger>
                <SelectContent>{INSP_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Kategorie / Fachbereich">
              <Select value={form.category || "__none"} onValueChange={(v) => set("category", v === "__none" ? "" : v)}>
                <SelectTrigger data-testid="insp-category"><SelectValue placeholder="Kategorie wählen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— keine —</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Bereich"><Input value={form.area} onChange={(e) => set("area", e.target.value)} data-testid="insp-area" /></Field>
            <Field label="Gebäude"><Input value={form.building} onChange={(e) => set("building", e.target.value)} data-testid="insp-building" /></Field>
            <Field label="Etage"><Input value={form.floor} onChange={(e) => set("floor", e.target.value)} data-testid="insp-floor" /></Field>
            <Field label="Raum"><Input value={form.room} onChange={(e) => set("room", e.target.value)} data-testid="insp-room" /></Field>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Hersteller"><Input value={form.manufacturer} onChange={(e) => set("manufacturer", e.target.value)} /></Field>
            <Field label="Modell"><Input value={form.model} onChange={(e) => set("model", e.target.value)} /></Field>
            <Field label="Seriennr."><Input value={form.serial_number} onChange={(e) => set("serial_number", e.target.value)} /></Field>
            <Field label="Inventarnr."><Input value={form.inventory_number} onChange={(e) => set("inventory_number", e.target.value)} /></Field>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Abteilung"><Input value={form.department} onChange={(e) => set("department", e.target.value)} /></Field>
            <Field label="Verantwortlicher"><Input value={form.responsible} onChange={(e) => set("responsible", e.target.value)} data-testid="insp-responsible" /></Field>
            <Field label="Dienstleister / Prüfer"><Input value={form.service_provider} onChange={(e) => set("service_provider", e.target.value)} /></Field>
            <Field label="Ansprechpartner"><Input value={form.contact} onChange={(e) => set("contact", e.target.value)} /></Field>
          </div>

          {assets.length > 0 && (
            <Field label={`Anlagen / Geräte zuordnen (${form.asset_ids.length})`}>
              <ScrollArea className="h-28 rounded-lg border border-slate-200 p-2">
                {assets.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer" data-testid={`insp-asset-${a.id}`}>
                    <Checkbox checked={form.asset_ids.includes(a.id)} onCheckedChange={() => toggleAsset(a.id)} />
                    <span className="truncate">{a.name}{a.room ? ` · ${a.room}` : ""}</span>
                  </label>
                ))}
              </ScrollArea>
            </Field>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Field label="Letztes Prüfdatum"><Input type="date" value={form.last_inspection_date || ""} onChange={(e) => set("last_inspection_date", e.target.value)} data-testid="insp-last-date" /></Field>
            <Field label="Intervall">
              <Select value={form.interval_type} onValueChange={(v) => set("interval_type", v)}>
                <SelectTrigger data-testid="insp-interval"><SelectValue /></SelectTrigger>
                <SelectContent>{INTERVALS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Nächste Fälligkeit"><Input type="date" value={form.next_due || ""} onChange={(e) => set("next_due", e.target.value)} data-testid="insp-next-due" /></Field>
          </div>

          {form.interval_type === "benutzerdefiniert" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Alle"><Input type="number" min={1} value={form.interval_value} onChange={(e) => set("interval_value", e.target.value)} data-testid="insp-interval-value" /></Field>
              <Field label="Einheit">
                <Select value={form.interval_unit} onValueChange={(v) => set("interval_unit", v)}>
                  <SelectTrigger data-testid="insp-interval-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>{INTERVAL_UNITS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
          )}

          {!form.next_due && preview && !isOneTime && (
            <p className="text-xs text-blue-600" data-testid="insp-next-preview">Nächste Fälligkeit wird berechnet: <b>{preview}</b></p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Priorität">
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="insp-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{INSP_PRIORITIES.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <label className="flex items-center gap-2 mt-6 text-sm cursor-pointer">
              <Switch checked={form.requires_protocol} onCheckedChange={(v) => set("requires_protocol", v)} data-testid="insp-requires-protocol" />
              Prüfprotokoll erforderlich
            </label>
          </div>

          <Field label="Bemerkungen"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></Field>
          </>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" disabled={saving} data-testid="save-inspection-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Speichern" : "Anlegen")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
